import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Button, ProgressBar, Collapse } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFlask,
  faArrowUpRightFromSquare,
  faChevronDown,
  faChevronUp
} from "@fortawesome/free-solid-svg-icons";
import VariantPreview from "./variant-preview";
import {
  Stat,
  StatGrid,
  TestCard,
  TextValue,
  SectionTitle,
  TimelineList,
  VariantLabel,
  VariantColumn
} from "./split-test.styles";
import {
  formatDays,
  formatDaysRunning,
  formatNumber,
  formatPValue,
  formatPercent,
  formatSignedPercent
} from "./split-test.helpers";
import { WebsiteStatusBadge } from "../common-styling";
import {
  STATUS_BADGE,
  describeValue,
  ELEMENT_TYPES
} from "./split-test.helpers";
import {
  generateDateTime,
  generateSiteUrl
} from "../../services/common-service";
import { hasAccess } from "../../services/access";
import { ACCESS_CONFIG, SPLIT_TEST_STATUS } from "../strings";

const previewUrl = (funnel, variant) => {
  const base = generateSiteUrl(funnel.domain, funnel.subDomain);
  if (!base) return null;
  const joiner = base.includes("?") ? "&" : "?";
  return `${base}${joiner}testMode=true&splitTestVariant=${variant}`;
};

const RunningTestCard = ({
  test,
  elementLabel,
  elementType = ELEMENT_TYPES.TEXT,
  onEvaluate,
  onCancel,
  busy
}) => {
  const { t } = useTranslation();
  const [showLog, setShowLog] = useState(false);
  const canAct = hasAccess(ACCESS_CONFIG.ADMIN_TIER);
  const stats = test.stats || {};
  const A = stats.A || {};
  const B = stats.B || {};
  const n = test.requiredSamplePerVariant;
  const look = stats.look || 0;
  const target = n ? n * Math.min(look + 1, 2) : null;
  const minExposures = Math.min(A.exposures || 0, B.exposures || 0);
  const progress = target ? Math.min(100, (minExposures / target) * 100) : 0;
  const sample = test.sampleFunnels?.[0];
  // before the first evaluation the job has not written stats yet
  const daysRunning =
    stats.daysRunning ??
    (test.startedAt ? (Date.now() - new Date(test.startedAt)) / 86400000 : 0);
  const liftColor =
    stats.lift > 0 ? "#198754" : stats.lift < 0 ? "#dc3545" : undefined;

  return (
    <TestCard className="p-3 mb-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faFlask} className="text-primary" />
            <span className="fw-bold fs-5">{elementLabel}</span>
            <WebsiteStatusBadge
              $stage={STATUS_BADGE[SPLIT_TEST_STATUS.RUNNING]}
            >
              {t("splitTests.status.RUNNING")}
            </WebsiteStatusBadge>
          </div>
          <div className="text-muted small mt-1">
            {t("splitTests.running.since", {
              date: generateDateTime(test.startedAt),
              days: formatDaysRunning(daysRunning)
            })}
            {" · "}
            {t("splitTests.running.eligible", {
              count: test.eligibleFunnelCount ?? test.eligibleFunnelCountAtStart
            })}
          </div>
        </div>
        {canAct && (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              disabled={busy}
              onClick={onEvaluate}
            >
              {t("splitTests.actions.evaluateNow")}
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              disabled={busy}
              onClick={onCancel}
            >
              {t("splitTests.actions.cancel")}
            </Button>
          </div>
        )}
      </div>

      {test.hypothesis && (
        <div className="mt-3">
          <SectionTitle>{t("splitTests.fields.hypothesis")}</SectionTitle>
          <div>{test.hypothesis}</div>
        </div>
      )}

      <Row className="mt-3 g-3">
        {[
          { key: "A", value: test.controlValue, data: A },
          { key: "B", value: test.variantValue, data: B }
        ].map(({ key, value, data }) => (
          <Col md={6} key={key}>
            <VariantColumn
              $winner={
                key === "B" ? stats.lift > 0 && stats.pValue < 0.0294 : false
              }
            >
              <div className="d-flex justify-content-between align-items-center">
                <VariantLabel $variant={key}>
                  <span className="chip">{key}</span>
                  {t(`splitTests.variant.${key}`)}
                </VariantLabel>
                {sample && previewUrl(sample, key) && (
                  <a
                    className="small"
                    href={previewUrl(sample, key)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("splitTests.actions.preview")}{" "}
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </a>
                )}
              </div>
              <TextValue className="mt-2">
                {elementType === ELEMENT_TYPES.COLOR && (
                  <span
                    className="d-inline-block me-2 align-middle rounded"
                    style={{ width: 16, height: 16, background: value }}
                  />
                )}
                {describeValue(elementType, value, t)}
              </TextValue>
              <StatGrid $cols={3}>
                <Stat>
                  <div className="label">{t("splitTests.stats.exposures")}</div>
                  <div className="value">
                    {formatNumber(data.exposures ?? 0)}
                  </div>
                </Stat>
                <Stat>
                  <div className="label">
                    {t("splitTests.stats.conversions")}
                  </div>
                  <div className="value">
                    {formatNumber(data.conversions ?? 0)}
                  </div>
                </Stat>
                <Stat>
                  <div className="label">{t("splitTests.stats.rate")}</div>
                  <div className="value">{formatPercent(data.rate)}</div>
                </Stat>
              </StatGrid>
              <VariantPreview
                element={test.element}
                value={value}
                funnelType={test.funnelType}
              />
            </VariantColumn>
          </Col>
        ))}
      </Row>

      <StatGrid $cols={4} className="mt-3">
        <Stat $big $color={liftColor}>
          <div className="label">{t("splitTests.stats.lift")}</div>
          <div className="value">{formatSignedPercent(stats.lift)}</div>
          <div className="sub">
            {t("splitTests.stats.ci")}: {formatSignedPercent(stats.ciLow)} …{" "}
            {formatSignedPercent(stats.ciHigh)}
          </div>
        </Stat>
        <Stat $big>
          <div className="label">{t("splitTests.stats.pValue")}</div>
          <div className="value">{formatPValue(stats.pValue)}</div>
          <div className="sub">{t("splitTests.stats.threshold")}</div>
        </Stat>
        <Stat $big>
          <div className="label">{t("splitTests.stats.sampleSize")}</div>
          <div className="value">{n ? formatNumber(n) : "–"}</div>
          <div className="sub">
            {n
              ? t("splitTests.stats.baseline", {
                  rate: formatPercent(test.baselineRateAtStart, 0),
                  mde: formatPercent(test.mdeRelative, 0)
                })
              : t("splitTests.stats.sampleAfter48h")}
          </div>
        </Stat>
        <Stat $big>
          <div className="label">{t("splitTests.stats.projectedDays")}</div>
          <div className="value">{formatDays(stats.projectedDays)}</div>
          <div className="sub">
            {t("splitTests.stats.lookOf", { look: Math.min(look + 1, 2) })}
          </div>
        </Stat>
      </StatGrid>

      <div className="mt-3">
        <div className="d-flex justify-content-between small text-muted mb-1">
          <span>
            {t("splitTests.stats.progress", {
              current: formatNumber(minExposures),
              target: target ? formatNumber(target) : "–"
            })}
          </span>
          <span>{Math.round(progress)} %</span>
        </div>
        <ProgressBar
          now={progress}
          variant={progress >= 100 ? "success" : "primary"}
        />
        <div className="small text-muted mt-1">
          {t("splitTests.stats.rule")}
        </div>
      </div>

      {test.decisionLog?.length > 0 && (
        <div className="mt-3">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            onClick={() => setShowLog(!showLog)}
          >
            <FontAwesomeIcon icon={showLog ? faChevronUp : faChevronDown} />{" "}
            {t("splitTests.running.log")} ({test.decisionLog.length})
          </Button>
          <Collapse in={showLog}>
            <div>
              <TimelineList className="mt-2">
                {[...test.decisionLog].reverse().map((entry, idx) => (
                  <li key={idx}>
                    <span className="when">{generateDateTime(entry.at)}</span>
                    <span>
                      {t(`splitTests.log.${entry.decision}`, {
                        defaultValue: entry.decision
                      })}
                      {entry.pValue !== undefined && entry.pValue !== null
                        ? ` · p = ${formatPValue(entry.pValue)}`
                        : ""}
                      {entry.note ? ` · ${entry.note}` : ""}
                    </span>
                  </li>
                ))}
              </TimelineList>
            </div>
          </Collapse>
        </div>
      )}
    </TestCard>
  );
};

export default RunningTestCard;
